import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Edit2, Trash2, MessageSquare, TrendingUp } from "lucide-react";
import { API } from "@/App";
import { formatDateTR } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

// Mask name for privacy (e.g., "Haluk Öztürk" -> "H**** Ö****")
function maskName(fullName) {
  if (!fullName) return "Anonim";
  
  const parts = fullName.trim().split(" ");
  return parts.map(part => {
    if (part.length <= 1) return part;
    return part.charAt(0) + "*".repeat(Math.min(part.length - 1, 4));
  }).join(" ");
}

// Star Rating Component
function StarRating({ rating, onRatingChange, readOnly = false, size = "md" }) {
  const [hoverRating, setHoverRating] = useState(0);
  
  const starSize = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-8 h-8" : "w-6 h-6";
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readOnly}
          className={`${readOnly ? "cursor-default" : "cursor-pointer"} transition-colors`}
          onMouseEnter={() => !readOnly && setHoverRating(star)}
          onMouseLeave={() => !readOnly && setHoverRating(0)}
          onClick={() => !readOnly && onRatingChange && onRatingChange(star)}
        >
          <Star
            className={`${starSize} ${
              star <= (hoverRating || rating)
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// Rating Distribution Bar
function RatingBar({ count, total, stars }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-12">{stars} yıldız</span>
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-yellow-400 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-8 text-grey">{count}</span>
    </div>
  );
}

// Review Card
function ReviewCard({ review, currentUserId, onEdit, onDelete }) {
  const isOwner = review.user_id === currentUserId;
  const displayName = isOwner ? review.user_name : maskName(review.user_name);
  
  return (
    <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10">
            <AvatarFallback className="bg-navy text-white">
              {review.user_name?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{displayName || "Anonim"}</p>
            <div className="flex items-center gap-2">
              <StarRating rating={review.rating} readOnly size="sm" />
              <span className="text-xs text-grey">
                {formatDateTR(review.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        {isOwner && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onEdit(review)}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => onDelete(review.review_id)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      
      {review.comment && (
        <p className="mt-3 text-sm text-grey">{review.comment}</p>
      )}
      
      {review.updated_at && (
        <p className="mt-2 text-xs text-slate-400">
          (düzenlendi)
        </p>
      )}
    </div>
  );
}

// Main Reviews Section Component
export default function ReviewsSection({ 
  targetType, // "business" or "trainer"
  targetId,
  targetName,
  currentUserId,
  showCreateButton = true 
}) {
  const { t } = useTranslation(["common", "messaging", "evaluations"]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, average: 0, distribution: {} });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [newRating, setNewRating] = useState(5);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (targetId) {
      fetchReviews();
    }
  }, [targetId]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const endpoint = targetType === "business" 
        ? `${API}/businesses/${targetId}/reviews`
        : `${API}/trainers/${targetId}/reviews`;
      
      const response = await fetch(endpoint, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews);
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (newRating < 1) {
      toast.error(t("common:toast_lutfenBirPuanSecin"));
      return;
    }

    setSubmitting(true);
    try {
      const url = editingReview 
        ? `${API}/reviews/${editingReview.review_id}`
        : `${API}/reviews`;
      
      const response = await fetch(url, {
        method: editingReview ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          review_type: targetType,
          target_id: targetId,
          rating: newRating,
          comment: newComment,
        }),
      });

      if (response.ok) {
        toast.success(editingReview ? t("evaluations:reviewUpdated") : t("evaluations:reviewSaved"));
        setShowCreateModal(false);
        setEditingReview(null);
        setNewRating(5);
        setNewComment("");
        fetchReviews();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setNewRating(review.rating);
    setNewComment(review.comment || "");
    setShowCreateModal(true);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm(t("evaluations:confirmDeleteReview"))) {
      return;
    }

    try {
      const response = await fetch(`${API}/reviews/${reviewId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success(t("evaluations:toast_degerlendirmeSilindi"));
        fetchReviews();
      } else {
        toast.error(t('members:deleteFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const hasUserReviewed = reviews.some(r => r.user_id === currentUserId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-lg flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Değerlendirmeler ({stats.total})
        </h3>
        {showCreateButton && currentUserId && !hasUserReviewed && (
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-navy hover:bg-navy/90 rounded-xl"
            data-testid="create-review-btn"
          >
            <Star className="w-4 h-4 mr-2" />
            Değerlendir
          </Button>
        )}
      </div>

      {/* Stats Card */}
      {stats.total > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Average Rating */}
              <div className="flex flex-col items-center justify-center">
                <p className="text-5xl font-bold text-navy">{stats.average}</p>
                <StarRating rating={Math.round(stats.average)} readOnly size="lg" />
                <p className="text-sm text-grey mt-2">{stats.total} değerlendirme</p>
              </div>
              
              {/* Distribution */}
              <div className="space-y-2">
                {[5, 4, 3, 2, 1].map((star) => (
                  <RatingBar 
                    key={star}
                    stars={star}
                    count={stats.distribution[star] || 0}
                    total={stats.total}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8 text-grey">{t('common:loading')}</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8 text-grey">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>{t("evaluations:noEvaluationsYet")}</p>
          {currentUserId && (
            <p className="text-sm mt-2">İlk değerlendirmeyi siz yapın!</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <ReviewCard
              key={review.review_id}
              review={review}
              currentUserId={currentUserId}
              onEdit={handleEditReview}
              onDelete={handleDeleteReview}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Review Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          setEditingReview(null);
          setNewRating(5);
          setNewComment("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingReview ? t('common:editReview') : `${targetName} ${t('common:evaluate')}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-grey">{t("common:puaniniz")}</p>
              <StarRating 
                rating={newRating} 
                onRatingChange={setNewRating}
                size="lg"
              />
              <p className="text-lg font-semibold text-navy">{newRating} / 5</p>
            </div>

            <div>
              <p className="text-sm text-grey mb-2">Yorumunuz (opsiyonel)</p>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={t("common:ph_deneyiminiziPaylasin")}
                rows={4}
                className="rounded-xl"
                data-testid="review-comment-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowCreateModal(false)}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleSubmitReview}
                disabled={submitting}
                data-testid="submit-review-btn"
              >
                {submitting ? "Kaydediliyor..." : (editingReview ? t('settings:update') : t('common:send'))}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Export Star Rating for reuse
export { StarRating };
